import { format, isSameYear } from 'date-fns';
import { TFunction } from 'i18next';
import { Timestamp } from 'shared/types/timestamp';

export type Options = {
  startDate?: Timestamp;
  endDate?: Timestamp;
  isShort?: boolean;
  today: Timestamp;
  t: TFunction;
  dateFormat?: string;
};

export const getStartTillEndLabel = ({
  startDate,
  endDate,
  isShort,
  today,
  t,
  dateFormat,
}: Options) => {
  const isMonthFirst = dateFormat?.toLowerCase().startsWith('m');
  const dayAndMonthFormat = isMonthFirst ? 'MMM d' : 'd MMM';
  const fullDateFormat = isMonthFirst
    ? `MMM d${isShort ? " ''yy" : ' yyyy'}`
    : `d MMM${isShort ? " ''yy" : ' yyyy'}`;

  const fallbackStartLabel = isShort
    ? undefined
    : t('general.labels.no-start-date');
  const startLabel = startDate
    ? format(
        startDate,
        isSameYear(today, startDate) ? dayAndMonthFormat : fullDateFormat,
      )
    : fallbackStartLabel;

  const fallbackEndLabel = isShort
    ? undefined
    : t('general.labels.no-deadline');
  const endLabel = endDate
    ? format(
        endDate,
        isSameYear(today, endDate) ? dayAndMonthFormat : fullDateFormat,
      )
    : fallbackEndLabel;

  if (!startDate && !endDate) {
    return t('general.labels.no-schedule');
  }

  // Check for compact format: Same month and year, both dates exist
  if (
    startDate &&
    endDate &&
    isSameYear(startDate, endDate) &&
    isSameYear(today, startDate) &&
    format(startDate, 'M') === format(endDate, 'M')
  ) {
    if (isMonthFirst) {
      // Format: "MMM d-d" (e.g., "Feb 16-22")
      return `${format(startDate, 'MMM d')}-${format(endDate, 'd')}`;
    } else {
      // Format: "d-d MMM" (e.g., "16-22 Feb")
      return `${format(startDate, 'd')}-${format(endDate, 'd MMM')}`;
    }
  }

  return [startLabel, endLabel].filter(Boolean).join(' - ');
};
